/**
 * ClientConfig — 来自 `GET {g}/config/client` 的纯展示参数。
 *
 * Server handler: `server/internal/httpapi/config_client_handler.go`
 *
 * 字段命名保持 snake_case 与后端 JSON 对齐（避免在 hook / API 层做转换 +
 * 让 TS 类型直接对应 spec 文档）。
 *
 * 全部字段为只读且 *仅展示用* — 这里返回的数值不参与服务端鉴权 / 不影响业务
 * 决策，只用于：PWA install banner 冷却天数、消息 SLA 文案、上传分片大小提示、
 * 资源包到期选项下拉框等。
 */
export type ClientConfig = {
  /** PWA install / refresh prompt 客户端配置 */
  pwa_client: {
    /**
     * 用户 dismiss install banner 后，冷却多少天再尝试再次弹出。
     * 默认 14（与 install-prompt.tsx 原硬编码一致）。
     */
    install_dismiss_cooldown_days: number
    /**
     * presigned URL（如下载链接）距离过期多少秒前主动 refresh。
     * 例：3600 = 过期前 1h 触发重签。
     */
    presigned_refresh_lead_seconds: number
  }

  /** 消息中心 SLA 文案 — service / user 客服回复时效 */
  messaging: {
    sla: {
      /** 工作时间内首次回复目标（分钟）— 文案展示 */
      first_reply_target_minutes: number
      /** 平均回复时长（分钟）— 文案展示 */
      avg_reply_minutes: number
    }
  }

  /** 审计日志展示用保留期（用户中心可见的"删除即销毁"文案等） */
  audit_retention: {
    display: {
      /** "您的操作记录将保留 N 天" — 仅文案 */
      user_visible_days: number
    }
  }

  /** 资源包到期选项（下拉框可选项） */
  pack: {
    /** ['7d', '30d', '90d', 'forever'] 等 — 与后端枚举对齐 */
    expires_at_options: ReadonlyArray<string>
  }

  /** locale 白名单（语言设置下拉框） */
  locales?: ReadonlyArray<string>
}

/**
 * 客户端兜底默认值。
 *
 * - 当 `{g}/config/client` 请求失败 / 返 5xx / 启动期 race condition 时，
 *   hook 返回此对象避免 UI 卡 loading 态。
 * - 与后端 `httpapi/config_client_handler.go` 的 *硬编码默认值* 保持同步；
 *   若后端默认值变更，本文件须同步 PR。
 */
export const DEFAULT_CLIENT_CONFIG: ClientConfig = {
  pwa_client: {
    install_dismiss_cooldown_days: 14,
    presigned_refresh_lead_seconds: 3600,
  },
  messaging: {
    sla: {
      first_reply_target_minutes: 30,
      avg_reply_minutes: 60,
    },
  },
  audit_retention: {
    display: {
      user_visible_days: 90,
    },
  },
  pack: {
    expires_at_options: ['7d', '30d', '90d', 'forever'],
  },
  locales: ['zh-CN', 'zh-TW', 'en', 'ja', 'ko'],
}
